import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const NO_COACH = "__none__";

/**
 * Coach / admin "edit member info" dialog.
 *
 * Fields shown depend on the actor's role:
 *   • admin   → name, email, role (coach picker enabled), weight unit
 *   • coach   → name, email, weight unit  (coach picker hidden — backend won't let
 *                                          coaches reassign athletes anyway)
 *
 * Athletes never see this — gated at the People page.
 */
export default function EditUserDialog({ user, onSaved, trigger }) {
  const { user: actor } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [coaches, setCoaches] = useState([]);
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    coach_id: user.coach_id || NO_COACH,
    weight_unit: user.weight_unit || "lb",
  });

  // Re-init when re-opening.
  useEffect(() => {
    if (open) {
      setForm({
        name: user.name || "",
        email: user.email || "",
        coach_id: user.coach_id || NO_COACH,
        weight_unit: user.weight_unit || "lb",
      });
    }
  }, [open, user]);

  // Admins can reassign coaches — fetch the coach list lazily.
  useEffect(() => {
    if (open && actor?.role === "admin" && user.role === "athlete" && coaches.length === 0) {
      api.get("/users", { params: { role: "coach" } })
        .then((r) => setCoaches(r.data))
        .catch(() => {});
    }
  }, [open, actor, user.role, coaches.length]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const patch = {};
      const trimmedName = form.name.trim();
      const trimmedEmail = form.email.trim().toLowerCase();
      if (trimmedName && trimmedName !== user.name) patch.name = trimmedName;
      if (trimmedEmail && trimmedEmail !== user.email) patch.email = trimmedEmail;
      if (form.weight_unit !== (user.weight_unit || "lb")) patch.weight_unit = form.weight_unit;
      if (actor?.role === "admin" && user.role === "athlete") {
        const cur = user.coach_id || NO_COACH;
        if (form.coach_id !== cur) {
          patch.coach_id = form.coach_id === NO_COACH ? "" : form.coach_id;
        }
      }
      if (Object.keys(patch).length === 0) {
        toast.info("Nothing to update");
        setOpen(false);
        setBusy(false);
        return;
      }
      const { data } = await api.patch(`/users/${user.id}`, patch);
      toast.success(`Updated ${data.name}`);
      onSaved?.(data);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const canEditCoach = actor?.role === "admin" && user.role === "athlete";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <span onClick={() => setOpen(true)} className="inline-flex" data-testid={`edit-user-trigger-${user.id}`}>
        {trigger || (
          <Button size="sm" variant="ghost" className="rounded-lg" title="Edit member info"
                  aria-label={`Edit info for ${user.name}`}>
            <PencilSimple size={14} className="text-ink-2" />
          </Button>
        )}
      </span>
      <DialogContent className="rounded-2xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <PencilSimple size={18} weight="fill" className="text-moss" />
            Edit member info
          </DialogTitle>
          <DialogDescription>
            Updating {user.name}. Email changes affect sign-in immediately.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="edit-user-form">
          <div>
            <Label className="hs-label" htmlFor="edit-user-name">Full name</Label>
            <Input
              id="edit-user-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-2 h-11 rounded-xl"
              data-testid="edit-user-name"
              required
            />
          </div>
          <div>
            <Label className="hs-label" htmlFor="edit-user-email">Email</Label>
            <Input
              id="edit-user-email"
              type="email"
              autoComplete="off"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-2 h-11 rounded-xl"
              data-testid="edit-user-email"
              required
            />
          </div>
          {canEditCoach && (
            <div>
              <Label className="hs-label">Assigned coach</Label>
              <Select value={form.coach_id} onValueChange={(v) => setForm({ ...form, coach_id: v })}>
                <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="edit-user-coach">
                  <SelectValue placeholder="Pick a coach" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_COACH}>— Unassigned —</SelectItem>
                  {coaches.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-ink-3 mt-1">
                Coaches can only see athletes assigned to them.
              </p>
            </div>
          )}
          <div>
            <Label className="hs-label">Default weight unit</Label>
            <Select value={form.weight_unit} onValueChange={(v) => setForm({ ...form, weight_unit: v })}>
              <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="edit-user-weight-unit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lb">Pounds (lb)</SelectItem>
                <SelectItem value="kg">Kilograms (kg)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="rounded-xl bg-moss hover:bg-moss-hover text-white hover:text-white"
              data-testid="edit-user-save"
            >
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
